import React, { memo } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { parse, isValid, differenceInCalendarDays } from 'date-fns';
import type { UrgentTarget } from '../../../../domain/dailyShiftSlot';
import { ModalContent } from '../MonthlyShiftSlotSetup.styles';

/**
 * 急募一覧モーダルのProps
 */
export interface UrgentTargetListModalProps {
  /** モーダルの開閉状態 */
  open: boolean;
  /** 急募対象枠一覧 */
  targets: UrgentTarget[];
  /** 一覧取得中フラグ */
  isLoading: boolean;
  /** 閉じる時のコールバック */
  onClose: () => void;
  /** 「急募を設定する」クリック時のコールバック */
  onOpenUrgentSettings: () => void;
  /** 「詳細」クリック時のコールバック */
  onOpenDetail: (target: UrgentTarget) => void;
}

/**
 * 曜日名
 */
const DAY_OF_WEEK_NAMES = ['日', '月', '火', '水', '木', '金', '土'];

/**
 * 診療日時ラベルを生成（YYYY/MM/DD（曜）HH:MM〜HH:MM）
 */
const formatTargetDateTime = (target: UrgentTarget): string => {
  const dateObj = parse(target.date, 'yyyy-MM-dd', new Date());
  const dateLabel = isValid(dateObj)
    ? `${target.date.replace(/-/g, '/')}（${DAY_OF_WEEK_NAMES[dateObj.getDay()]}）`
    : target.date;
  return `${dateLabel} ${target.startTime}〜${target.endTime}`;
};

/**
 * 診療日まで1週間以内の枠に適用するインセンティブ表示額（円）
 */
const URGENT_INCENTIVE_WITHIN_ONE_WEEK = 2000;

/**
 * 一覧に表示するインセンティブ金額を算出
 *
 * 募集済み枠数が0の枠は診療日までの残日数で表示額を切り替える:
 * - 診療日まで8日以上先（1週間より前）: ¥0
 * - 診療日まで7日以内（当日含む）: 設定値があればその値、なければ ¥2,000
 * 募集済み枠数が1以上の枠は設定値をそのまま表示（未設定は null = 「−」）
 */
const getDisplayIncentive = (target: UrgentTarget): number | null => {
  if (target.recruitedSlots > 0) {
    return target.incentive;
  }
  const dateObj = parse(target.date, 'yyyy-MM-dd', new Date());
  if (!isValid(dateObj)) {
    return target.incentive;
  }
  const daysUntil = differenceInCalendarDays(dateObj, new Date());
  if (daysUntil > 7) {
    return 0;
  }
  return target.incentive ?? URGENT_INCENTIVE_WITHIN_ONE_WEEK;
};

/**
 * インセンティブ列の表示ラベルを生成
 */
const formatIncentiveLabel = (target: UrgentTarget): string => {
  const displayIncentive = getDisplayIncentive(target);
  return displayIncentive != null ? `¥${displayIncentive.toLocaleString()}` : '−';
};

/**
 * 急募一覧モーダルコンポーネント
 *
 * - 急募対象枠を一覧表示（診療日時・インセンティブ・募集枠数・確定数・確定医師・LINE通知・詳細）
 * - 「急募を設定する」ボタンから既存の急募設定画面を開く
 * - 募集済み枠数0の枠はインセンティブを残日数で切替表示（8日以上先: ¥0 / 7日以内: 設定値または¥2,000）
 */
const UrgentTargetListModal: React.FC<UrgentTargetListModalProps> = memo(({
  open,
  targets,
  isLoading,
  onClose,
  onOpenUrgentSettings,
  onOpenDetail,
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent sx={{ minWidth: 900, maxWidth: 1000 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            急募一覧
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={onOpenUrgentSettings}
            >
              急募を設定する
            </Button>
            <Button
              onClick={onClose}
              size="small"
              sx={{ minWidth: 'auto' }}
            >
              <CloseIcon />
            </Button>
          </Box>
        </Box>

        {/* 一覧テーブル */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 480 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>診療日時</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 100 }}>
                    インセンティブ
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 70 }}>
                    募集枠数
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 70 }}>
                    確定数
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>確定医師</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 90 }}>
                    LINE通知
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 80 }}>
                    詳細
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {targets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                      急募対象の枠はありません
                    </TableCell>
                  </TableRow>
                ) : (
                  targets.map((target) => (
                    <TableRow key={target.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {formatTargetDateTime(target)}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {formatIncentiveLabel(target)}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {`${target.recruitmentSlots}枠`}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {`${target.confirmedSlots}名`}
                      </TableCell>
                      <TableCell>
                        {target.confirmedDoctors.length > 0
                          ? target.confirmedDoctors.map((doctor) => doctor.name).join('、')
                          : '−'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {target.urgentNotifiedAt ? (
                          <Chip label="送信済み" color="success" size="small" />
                        ) : (
                          <Chip label="未送信" color="error" size="small" />
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => onOpenDetail(target)}
                        >
                          詳細
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            閉じる
          </Button>
        </Box>
      </ModalContent>
    </Modal>
  );
});

UrgentTargetListModal.displayName = 'UrgentTargetListModal';

export default UrgentTargetListModal;
